// RiskRowDetails.jsx 🧩 Full Detail View per Risk — With Editable Treatment Plans
import React from "react";
import SmoothCollapse from "../SmoothCollapse";
import axios from "axios";
import "./RiskRowDetails.css";

const RiskRowDetails = ({
  risk,
  isExpanded,
  onEdit,
  onDelete,
  onRefresh,
  showToast,
  userCountry,
  onAddMesure,
  onAddTreatment,
  onEditTreatment,
}) => {
  const {
    id,
    name,
    criterion,
    business_unit,
    process,
    responsible,
    impact_score,
    likelihood_brut,
    likelihood_attenue,
    country_code,
    ressources = [],
    menaces = [],
    consequences = [],
    mesures = [],
    traitements = [],
    created_at,
    updated_at,
  } = risk;

  const isEditable = (country_code ?? "").toLowerCase() === (userCountry ?? "").toLowerCase();

  const getProgressColor = (value) => {
    if (value >= 90) return "bg-success";
    if (value >= 70) return "bg-info";
    if (value >= 40) return "bg-warning text-dark";
    return "bg-danger";
  };

  const handleRemoveMeasure = async (measureName) => {
    try {
      await axios.post("/api/unlink_mesure.php", { risk_id: id, mesure: measureName });
      showToast("Measure removed");
      onRefresh?.();
    } catch {
      showToast("Error removing measure", "danger");
    }
  };

  const handleDeleteTreatment = async (treatmentId) => {
    try {
      const res = await axios.post("/api/delete_treatment_plan.php", { id: treatmentId });
      if (res.data.success) {
        showToast("Treatment plan deleted");
        onRefresh?.();
      } else {
        showToast("Error deleting treatment plan", "danger");
      }
    } catch {
      showToast("Server error", "danger");
    }
  };

  return (
    <tr className="risk-details-row">
      <td colSpan="5" className="p-0">
        <SmoothCollapse show={isExpanded}>
          <div className="risk-details-container">
            {/* Header */}
            <div className="risk-details-header-bar">
              <div className="risk-header-inline justify-content-between">
                <div className="d-flex align-items-center flex-wrap">
                  <h6 className="risk-section-title mb-0">
                    <i className="bi bi-info-circle" /> Risk Details
                  </h6>
                  <div className="risk-meta-badges ms-3">
                    {created_at && (
                      <span className="badge bg-secondary me-2">
                        <i className="bi bi-calendar-event me-1" />
                        {created_at}
                      </span>
                    )}
                    {updated_at && (
                      <span className="badge bg-dark">
                        <i className="bi bi-arrow-repeat me-1" />
                        {updated_at}
                      </span>
                    )}
                  </div>
                </div>
                {isEditable && (
                  <div className="risk-actions-inline ms-auto">
                    <button className="btn-icon" onClick={() => onEdit(risk)} title="Edit">
                      <i className="bi bi-pencil-square color-orange" />
                    </button>
                    <button className="btn-icon" onClick={() => onDelete(risk)} title="Delete">
                      <i className="bi bi-trash text-danger" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Info & Metrics */}
            <div className="risk-grid-row meta-grid">
              <div className="risk-card">
                <div className="section-title"><i className="bi bi-info-square" /> General Info</div>
                <ul className="risk-list">
                  <li><strong>Name:</strong> {name}</li>
                  <li><strong>Criterion:</strong> {criterion}</li>
                  <li><strong>Responsible:</strong> {responsible}</li>
                  <li><strong>Business Unit:</strong> {business_unit}</li>
                  <li><strong>Process:</strong> {process}</li>
                </ul>
              </div>
              <div className="risk-card">
                <div className="section-title"><i className="bi bi-bar-chart" /> Metrics & Consequences</div>
                <ul className="risk-list">
                  <li><strong>Impact:</strong> {impact_score} / 4</li>
                  <li><strong>Raw Likelihood:</strong> {likelihood_brut} / 4</li>
                  <li><strong>Mitigated Likelihood:</strong> {likelihood_attenue} / 4</li>
                  {consequences.length > 0 && (
                    <li>
                      <strong>Consequences:</strong>
                      <div className="risk-consequence-badges">
                        {consequences.map((c, i) => (
                          <span key={i} className={`badge consequence-badge level-${c.score}`}>
                            {c.axe}: {c.score}
                          </span>
                        ))}
                      </div>
                    </li>
                  )}
                </ul>
              </div>
            </div>

            {/* Relations */}
            <div className="risk-grid-row">
              <div className="risk-card">
                <div className="section-title"><i className="bi bi-box" /> Resources</div>
                <ul className="risk-list">
                  {ressources.length ? ressources.map((r, i) => <li key={i}>{r}</li>) : <li className="text-danger">None</li>}
                </ul>
              </div>

              <div className="risk-card">
                <div className="section-title"><i className="bi bi-bug" /> Threats</div>
                <ul className="risk-list">
                  {menaces.length ? menaces.map((m, i) => <li key={i}>{m}</li>) : <li className="text-danger">None</li>}
                </ul>
              </div>

              <div className="risk-card">
                <div className="section-title d-flex justify-content-between align-items-center">
                  <span><i className="bi bi-capsule" /> Measures</span>
                  {isEditable && (
                    <button className="btn-add-inline" onClick={() => onAddMesure(risk)} title="Add measure">
                      <i className="bi bi-plus-circle color-orange" />
                    </button>
                  )}
                </div>
                <ul className="risk-list">
                  {mesures.length ? mesures.map((m, i) => (
                    <li key={i}>
                      <span>{m}</span>
                      {isEditable && (
                        <button className="btn-remove" onClick={() => handleRemoveMeasure(m)} title="Remove">
                          <i className="bi bi-x-circle"></i>
                        </button>
                      )}
                    </li>
                  )) : <li className="text-danger">None</li>}
                </ul>
              </div>
            </div>

            {/* 🛠 Treatment Plans */}
            <div className="risk-card full-width">
              <div className="section-title d-flex justify-content-between align-items-center">
                <span><i className="bi bi-clipboard-check" /> Treatment Plans</span>
                {isEditable && (
                  <button className="btn-add-inline" onClick={() => onAddTreatment(risk)} title="Add plan">
                    <i className="bi bi-plus-circle color-orange" />
                  </button>
                )}
              </div>

              <ul className="treatment-pill-list">
                {traitements.length ? traitements.map((t, i) => (
                  <li key={i} className="treatment-pill">
                    <div className="pill-title-line">
                      <strong className="pill-title">{t.title || "Untitled Plan"}</strong>

                      <div className="pill-sublist-inline">
                        {t.ressources?.length > 0 && (
                          <span className="sublist-group">
                            <span className="sublist-label"><i className="bi bi-box" /> Resources:</span>
                            {t.ressources.map((r, i) => (
                              <span key={i} className="sublist-item"><i className="bi bi-dot" /> {r}</span>
                            ))}
                          </span>
                        )}

                        {t.mesures?.length > 0 && (
                          <span className="sublist-group">
                            <span className="sublist-label"><i className="bi bi-capsule" /> Measures:</span>
                            {t.mesures.map((m, i) => (
                              <span key={i} className="sublist-item"><i className="bi bi-dot" /> {m}</span>
                            ))}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="pill-inline">
                      <span className="pill-badge bg-secondary">{t.action}</span>
                      <span className={`pill-badge ${
                        t.status === "Completed" ? "bg-success" :
                        t.status === "Ongoing" ? "bg-warning text-dark" : "bg-dark"
                      }`}>{t.status}</span>

                      {t.owner && <span className="pill-meta"><i className="bi bi-person" /> {t.owner}</span>}
                      {t.planned_date && <span className="pill-meta"><i className="bi bi-calendar-event" /> {t.planned_date}</span>}
                      {t.completion_date && <span className="pill-meta"><i className="bi bi-check2-circle" /> {t.completion_date}</span>}
                      {t.created_at && <span className="pill-meta"><i className="bi bi-clock-history" /> {t.created_at}</span>}
                      {typeof t.progress !== "undefined" && (
                        <div className="pill-progress">
                          <div
                            className={`pill-progress-bar progress-bar-striped progress-bar-animated ${getProgressColor(t.progress)}`}
                            style={{ width: `${t.progress}%` }}
                          >
                            {t.progress}%
                          </div>
                        </div>
                      )}
                      {isEditable && (
                        <div className="pill-actions">
                          <button className="btn-icon" onClick={() => onEditTreatment?.(t)} title="Edit">
                            <i className="bi bi-pencil text-warning" />
                          </button>
                          <button className="btn-icon" onClick={() => handleDeleteTreatment(t.id)} title="Delete">
                            <i className="bi bi-x-circle text-danger" />
                          </button>
                        </div>
                      )}
                    </div>

                    {t.justification && (
                      <div className="pill-justification mt-2">
                        <i className="bi bi-chat-left-text" /> {t.justification}
                      </div>
                    )}
                  </li>
                )) : (
                  <li className="text-danger">No treatment plans</li>
                )}
              </ul>
            </div>
          </div>
        </SmoothCollapse>
      </td>
    </tr>
  );
};

export default RiskRowDetails;
